import type { ColorSystemOptions } from '@mui/material/styles';

const WHITE_CONTRAST = 'white'; // #fff
const BLACK_CONTRAST = 'black';

/** Converts a hex color to rgba with the specified opacity (0-1) */
const withOpacity = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Color palette extracted from Figma design system
const RED_01 = '#F04465';
const red = {
  RED_01,
  RED_02: '#EF5683',
  RED_03: '#F17297',
  RED_04: '#F48EAC',
  RED_05: '#F7ABC1',
  RED_06: '#FAC7D6',
  RED_07: '#FCE3EA',
  RED_OPACITY: withOpacity(RED_01, 0.2),
};

const green = {
  GREEN_01: '#29A072',
  GREEN_02: '#48AE86',
  GREEN_03: '#66BB9A',
  GREEN_04: '#A1FFD8',
  GREEN_05: '#B0FFDF',
  GREEN_06: '#C0FFE5',
  GREEN_07: '#D0FFEC',
  // Figma "Data/Green" — the data-visualization green, brighter than the ladder above.
  GREEN_DATA: '#71FFC3',
  GREEN_OPACITY: 'rgba(145, 255, 210, 0.2)',
};

// const DARK_GREEN_01 = '#29A072';
// const darkGreen = {
//   DARK_GREEN_01,
//   DARK_GREEN_02: '#48AE86',
//   DARK_GREEN_03: '#66BB9A',
//   DARK_GREEN_04: '#85C9AE',
//   DARK_GREEN_05: '#A3D6C3',
//   DARK_GREEN_06: '#C2E4D7',
//   DARK_GREEN_07: '#E0F1EB',
//   DARK_GREEN_OPACITY_02: withOpacity(DARK_GREEN_01, 0.2),
//   DARK_GREEN_OPACITY_01: withOpacity(DARK_GREEN_01, 0.1),
// };

const YELLOW_01 = '#FFC647';
const yellow = {
  YELLOW_01,
  YELLOW_02: '#FFCE61',
  YELLOW_03: '#FFD67C',
  YELLOW_04: '#FFDE96',
  YELLOW_05: '#FFE7B0',
  YELLOW_06: '#FFEFCA',
  YELLOW_07: '#FFF7E5',
  YELLOW_OPACITY: withOpacity(YELLOW_01, 0.2),
};

const ORANGE_01 = '#FE5C00';
const orange = {
  ORANGE_01,
  ORANGE_02: '#FE7324',
  ORANGE_03: '#FE8B49',
  ORANGE_04: '#FEA26D',
  ORANGE_05: '#FFB992',
  ORANGE_06: '#FFD0B6',
  ORANGE_07: '#FFE8DB',
  ORANGE_OPACITY_02: withOpacity(ORANGE_01, 0.2),
  ORANGE_OPACITY_01: withOpacity(ORANGE_01, 0.1),
};

const BLUE_01 = '#008EDD';
const blue = {
  BLUE_01,
  BLUE_02: '#4F9ADE',
  BLUE_03: '#5FA8E2',
  BLUE_04: '#73B6E8',
  BLUE_05: '#88C5EC',
  BLUE_06: '#B9E1F6',
  BLUE_07: '#CAF1FD',
  BLUE_OPACITY: withOpacity(BLUE_01, 0.2),
  BLUE_OPACITY_60: withOpacity(BLUE_01, 0.6),
};

const GREY_01 = '#B1BBBF';
const grey = {
  GREY_01,
  GREY_02: '#CFD9DC',
  GREY_03: '#CFD9DC',
  GREY_04: '#DEE4E6',
  GREY_05: '#E5E9EB',
  GREY_06: '#EBEFF0',
  GREY_07: '#F2F4F5',
  GREY_OPACITY: withOpacity(GREY_01, 0.2),
};

const black = {
  BLACK_01: '#02151E',
  BLACK_02: 'rgba(255, 255, 255, 0.05)',
  BLACK_03: 'rgba(255, 255, 255, 0.10)',
};

const purple = {
  PURPLE_01: 'rgba(39, 39, 66, 0.20)',
  PURPLE_02: 'rgba(39, 39, 66, 0.90)',
};

// Main colors - Light theme (from Figma)
const mainLight = {
  CONTENT_PRIMARY: '#02151E',
  CONTENT_SECONDARY: '#505D63',
  DIVIDER: '#CFD9DC',
  TOOLTIPS: 'rgba(2, 21, 30, 0.9)',
  BACKGROUND_PRIMARY: '#FFFFFF',
  BACKGROUND_SECONDARY: '#F2F4F5',
  DISABLE_CONTENT: '#B1BBBF',
  DISABLE_BACKGROUND: '#E5E9EB',
  HOVER: '#E7F0F3',
  SECTIONS_BACKGROUNDS: '#EDEDED',
};

// Main colors - Dark theme (from Figma)
const mainDark = {
  CONTENT_PRIMARY: '#FFFFFF',
  CONTENT_SECONDARY: '#B1BBBF',
  DIVIDER: '#404244',
  TOOLTIPS: 'rgba(2, 21, 30, 0.9)',
  BACKGROUND_PRIMARY: '#2B2D30',
  BACKGROUND_SECONDARY: '#1E1F22',
  DISABLE_CONTENT: '#595959',
  DISABLE_BACKGROUND: '#3F3F3F',
  HOVER: '#768084',
  SECTIONS_BACKGROUNDS: '#131314',
};

// Special gradients (from Figma)
const gradients = {
  GRADIENT_01: 'linear-gradient(135deg, #CFCFE5, #F5F5F5)', // Angular gradient
  GRADIENT_02: 'linear-gradient(90deg, #F5F5F5 -30.29%, #CFCFE5 97.86%)',
  GRADIENT_03: 'linear-gradient(108deg, #FFF 2.25%, #E7F0F3 108.15%);',
  GRADIENT_AI: 'linear-gradient(to right, #C9EBAE, #0073B0)',
  GRADIENT_BACKUP_CONSOLE:
    'linear-gradient(70deg, #EA7345 10.7%, #EF92CA 70.2%)',
  GRADIENT_DATA_LAKE_CONSOLE: 'linear-gradient(90deg, #C9EBAE, #0073B0)',
  GRADIENT_VIOLATIONS:
    'linear-gradient(180deg, rgba(248, 122, 33, 0.2) 0%, rgba(251, 149, 110, 0.2) 100%)',
  GRADIENT_VIOLATIONS_ICON: 'linear-gradient(180deg, #F87A21 0%, #FB956E 100%)',
};

export const VaultColors = {
  PINK: '#FEB2F1',
  LIGHT_BLUE: '#8AE3FF',
  PURPLE: '#BDBEF7',
  PEACH: '#E3B2A1',
  BROWN: '#D1B9C0',
  GREEN: '#A1FFD8',
};

export const NativeVaultColor = '#CFD9DC';

const graphPink = {
  PINK_G_01: '#EDB6C9',
  PINK_G_02: '#F1D0F3',
  PINK_G_03: '#FBE2E5',
  PINK_G_04: '#E4BAD4',
  PINK_G_05: '#F2CDDA',
};

const graphLightBlue = {
  LIGHT_BLUE_G_01: '#B0E0E6',
  LIGHT_BLUE_G_02: '#B6CFED',
  LIGHT_BLUE_G_03: '#CDE7FF',
  LIGHT_BLUE_G_04: '#C2F0FC',
  LIGHT_BLUE_G_05: '#A5D8FF',
};

const graphPurple = {
  PURPLE_G_01: '#D0C4DF',
  PURPLE_G_02: '#E3E2FD',
  PURPLE_G_03: '#D8B4F8',
  PURPLE_G_04: '#E0D0F3',
  PURPLE_G_05: '#D9D7F1',
};

const graphPeach = {
  PEACH_G_01: '#F2D2CD',
  PEACH_G_02: '#E1D7C1',
  PEACH_G_03: '#FFD6A5',
  PEACH_G_04: '#F3DED0',
  PEACH_G_05: '#FFCBCB',
};

const graphBrown = {
  BROWN_G_01: '#E1C7C1',
  BROWN_G_02: '#EDBCB6',
  BROWN_G_03: '#EF9B8F',
  BROWN_G_04: '#EED0B4',
  BROWN_G_05: '#F7D5C1',
};

const graphGreen = {
  GREEN_G_01: '#D5E59A',
  GREEN_G_02: '#B2D3C2',
  GREEN_G_03: '#9AE5A7',
  GREEN_G_04: '#E2F8D1',
  GREEN_G_05: '#9AE5CB',
};

export const graphColors: Record<string, string[]> = {
  main: Object.values(VaultColors),
  pink: Object.values(graphPink),
  lightBlue: Object.values(graphLightBlue),
  purple: Object.values(graphPurple),
  peach: Object.values(graphPeach),
  brown: Object.values(graphBrown),
  green: Object.values(graphGreen),
};

// Base Figma palettes, exported so feature modules can compose their own fixed (theme-independent)
// color mappings against the canonical tokens instead of hardcoding hex.
export { blue, green, grey, yellow, orange, red };

export const graphOpacity = {
  warning60: withOpacity(YELLOW_01, 0.6),
  error60: withOpacity(red.RED_04, 0.6),
  fatal60: withOpacity(red.RED_01, 0.6),
  info10: withOpacity(BLUE_01, 0.1),
  info60: withOpacity(BLUE_01, 0.6),
  debug60: withOpacity(mainLight.DISABLE_CONTENT, 0.6),
};

// =============================================================================
// MUI COLOR SCHEMES (for theme.palette)
// Only flat color values - no nested objects allowed by MUI's extendTheme
// =============================================================================

const colorSchemes = (): Record<'light' | 'dark', ColorSystemOptions> => {
  const error = {
    main: red.RED_01, // MUI auto - TextField (error), Alert (severity="error"), Button (color="error")
    light: red.RED_03, // MUI auto - hover/light states for error components
    bgLightHover: red.RED_06,
    bgLight: red.RED_07,
    opacity: red.RED_OPACITY,
  };

  const warning = {
    main: yellow.YELLOW_01, // MUI auto - Alert (severity="warning"), Button (color="warning")
    bgLightHover: yellow.YELLOW_06,
    bgLight: yellow.YELLOW_07,
    contrastText: WHITE_CONTRAST, // MUI auto - text on warning backgrounds
    opacity: yellow.YELLOW_OPACITY,
  };

  const info = {
    main: blue.BLUE_01, // MUI auto - Alert (severity="info"), Button (color="info")
    contrastText: WHITE_CONTRAST, // MUI auto - text on info backgrounds
    opacity: blue.BLUE_OPACITY,
    opacity60: blue.BLUE_OPACITY_60,
  };

  const success = {
    main: green.GREEN_01, // MUI auto - Alert (severity="success"), Button (color="success")
    bgLightHover: green.GREEN_06,
    bgLight: green.GREEN_07,
    contrastText: WHITE_CONTRAST, // MUI auto - text on success backgrounds
    opacity: green.GREEN_OPACITY,
  };

  return {
    light: {
      palette: {
        custom: Object.entries({
          ...black,
          ...blue,
          ...green,
          ...grey,
          ...orange,
          ...red,
          ...yellow,
          ...purple,
          ...gradients,
          ...Object.fromEntries(
            Object.entries(mainDark).map(([k, v]) => [`MAIN_DARK_${k}`, v])
          ),
        }).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key.replace(/_/g, '-')] = value;
          return acc;
        }, {}),
        primary: {
          main: mainLight.CONTENT_PRIMARY, // MUI auto - Button, Checkbox, Radio, Switch, Link, Tabs, Fab
          light: '#A379FF', // MUI auto - hover states
          dark: '#E7F0F3', // MUI auto - active/pressed states
          hover: mainLight.HOVER,
          contrastText: 'white', // MUI auto - text on primary-colored backgrounds (Button, Fab, Chip)
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#8A8D93', // MUI auto - Button (color="secondary"), Checkbox, Radio
          light: '#A1A4A9', // MUI auto - hover states
          dark: '#7C7F84', // MUI auto - active/pressed states
          contrastText: BLACK_CONTRAST, // MUI auto - text on secondary-colored backgrounds
          lighterOpacity:
            'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
        },
        error,
        warning,
        info,
        success,
        text: {
          primary: mainLight.CONTENT_PRIMARY, // MUI auto - Typography default, most text content
          secondary: mainLight.CONTENT_SECONDARY, // MUI auto - Typography (color="textSecondary"), helper text
          disabled: mainLight.DISABLE_CONTENT, // MUI auto - disabled text states
        },
        divider: mainLight.DIVIDER, // MUI auto - Divider, Table borders
        background: {
          primary: mainLight.BACKGROUND_PRIMARY,
          secondary: mainLight.BACKGROUND_SECONDARY,
          default: '#F5F5F5', // MUI auto - page background (via CssBaseline)
          disabled: mainLight.DISABLE_BACKGROUND,
          section: mainLight.SECTIONS_BACKGROUNDS,
          editor: 'white',
          paper: 'white', // MUI auto - Paper, Card, Dialog, Menu, Popover surfaces
          paperChannel: '255 255 255',
          dark: mainLight.CONTENT_PRIMARY,
          pageGradient: gradients.GRADIENT_03,
        },
        action: {
          active: `rgb(var(--mui-mainColorChannels-light) / 0.6)`, // MUI auto - active icon color
          hover: `rgb(var(--mui-mainColorChannels-light) / 0.04)`, // MUI auto - hover background for ListItem, Button, IconButton
          selected: `rgb(var(--mui-mainColorChannels-light) / 0.08)`, // MUI auto - selected state background (ListItem, MenuItem)
          disabled: `rgb(var(--mui-mainColorChannels-light) / 0.3)`, // MUI auto - disabled state colors
          focus: `rgb(var(--mui-mainColorChannels-light) / 0.1)`, // MUI auto - focus state colors
          activeChannel: 'var(--mui-mainColorChannels-light)',
        },
        Tooltip: {
          bg: '#1A0E33',
          text: '#FFFFFF',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)', // MUI auto - Table cell borders
        },
        mainShadowChannel: '46 38 61',
      },
    },
    dark: {
      palette: {
        custom: Object.entries({
          ...black,
          ...blue,
          ...green,
          ...grey,
          ...orange,
          ...red,
          ...yellow,
          ...purple,
          ...gradients,
          ...Object.fromEntries(
            Object.entries(mainDark).map(([k, v]) => [`MAIN_DARK_${k}`, v])
          ),
        }).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key.replace(/_/g, '-')] = value;
          return acc;
        }, {}),
        primary: {
          main: mainDark.CONTENT_PRIMARY, // MUI auto - Button, Checkbox, Radio, Switch, Link, Tabs, Fab
          light: '#A379FF', // MUI auto - hover states
          dark: mainLight.CONTENT_PRIMARY, // MUI auto - active/pressed states
          hover: mainDark.HOVER,
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#8A8D93', // MUI auto - Button (color="secondary"), Checkbox, Radio
          light: '#A1A4A9', // MUI auto - hover states
          dark: '#7C7F84', // MUI auto - active/pressed states
          contrastText: WHITE_CONTRAST, // MUI auto - text on secondary-colored backgrounds
          lighterOpacity:
            'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
        },
        error,
        warning,
        info,
        success,
        text: {
          primary: mainDark.CONTENT_PRIMARY, // MUI auto - Typography default, most text content
          secondary: mainDark.CONTENT_SECONDARY, // MUI auto - Typography (color="textSecondary"), helper text
          disabled: mainDark.DISABLE_CONTENT, // MUI auto - disabled text states
        },
        divider: mainDark.DIVIDER, // MUI auto - Divider, Table borders
        background: {
          primary: mainDark.BACKGROUND_PRIMARY,
          secondary: mainDark.BACKGROUND_SECONDARY,
          default: '#17222c', // MUI auto - page background (via CssBaseline)
          disabled: mainDark.DISABLE_BACKGROUND,
          section: mainDark.SECTIONS_BACKGROUNDS,
          editor: '#1e1e1e',
          paper: '#192a35', // MUI auto - Paper, Card, Dialog, Menu, Popover surfaces
          paperChannel: '49 45 75',
          dark: '#F5F5F5',
          pageGradient: '#17222c',
        },
        action: {
          active: `rgb(var(--mui-mainColorChannels-dark) / 0.6)`, // MUI auto - active icon color
          hover: `rgb(var(--mui-mainColorChannels-dark) / 0.04)`, // MUI auto - hover background for ListItem, Button, IconButton
          selected: `rgb(var(--mui-mainColorChannels-dark) / 0.08)`, // MUI auto - selected state background (ListItem, MenuItem)
          disabled: `rgb(var(--mui-mainColorChannels-dark) / 0.3)`, // MUI auto - disabled state colors
          focus: `rgb(var(--mui-mainColorChannels-dark) / 0.1)`, // MUI auto - focus state colors
          activeChannel: 'var(--mui-mainColorChannels-dark)',
        },
        Tooltip: {
          bg: '#F7F4FF',
          text: '#312D4B',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)', // MUI auto - Table cell borders
        },
        mainShadowChannel: '19 17 32',
      },
    },
  };
};

export { colorSchemes };
